/*
  Rebuy-powered recommendations rendered with the theme's own product cards.

  A hidden Rebuy widget runs Rebuy's recommendation logic and reports the products through
  `rebuy.productsChange`. We ask Shopify to render each of those products through the
  `api-product-grid-item` section, so the card - siblings, swatches, badges - is the exact same
  card as on every other grid in the theme.

  Analytics: Rebuy only attributes what goes through its widget, so every product link on our
  cards is wired back to the widget instance:
  - href = widget.learnMoreURL(product): adds `_rdiscovery-handle` / `_rdiscovery-widget` /
    `_r_experimentId`, which Rebuy reads on the PDP to add `_source` / `_attribution` /
    `_widget_id` to the cart line - that is the revenue attribution and A/B experiment link.
  - click -> widget.learnMore(product, event): sends Rebuy's "tapped product" click event and
    the GA event, then navigates. It is the same call Rebuy's own widget template makes.
  The impression ("viewed widget") is sent by Rebuy itself when the hidden widget initialises.
*/
(() => {
  const CARD_SECTION = 'api-product-grid-item';

  const rootUrl = () => window.theme?.routes?.root || window.Shopify?.routes?.root || '/';

  const handleFromUrl = (url) => (url || '').split('/products/')[1]?.split(/[?#/]/)[0] || '';

  class RebuyRecommendations extends HTMLElement {
    constructor() {
      super();
      this.products = new Map();
      this.onProductsChange = this.onProductsChange.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.content = this.querySelector('[data-rebuy-content]');
      this.grid = this.querySelector('[data-rebuy-grid]');
      this.slider = this.querySelector('grid-slider');
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.excludeHandle = this.dataset.excludeHandle || '';

      if (!this.grid) return;

      document.addEventListener('rebuy.productsChange', this.onProductsChange);
      this.addEventListener('click', this.onClick);

      // A sibling swatch swaps a card for a freshly fetched one (product-card-sibling-swatch.js),
      // which arrives with plain links - re-wire whatever lands in the grid.
      this.observer = new MutationObserver(() => this.wireCards());
      this.observer.observe(this.grid, {childList: true});

      // Rebuy may have hydrated its widget before this element upgraded. Fresh widget nodes
      // (PDP sibling swap, theme editor re-render) are picked up by a single `Rebuy.init()` -
      // see pdp-sibling-swap.js and the `shopify:section:load` listener below.
      this.renderFromHydratedWidget();
    }

    disconnectedCallback() {
      document.removeEventListener('rebuy.productsChange', this.onProductsChange);
      this.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
      this.controller?.abort();
    }

    /*
      Matched by DOM, not by id: when a Rebuy A/B experiment runs on this widget, Rebuy puts a
      new div with the *variant's* widget id inside ours, so visitors in the variant group get
      events from an id the section settings never mention. Matching by element also keeps two
      sections that use the same widget id from rendering each other's products.

      Both nodes are checked because Rebuy mounts its Vue view on `widget.element`, and Vue 2
      replaces the mounted node: after the first render `widget.element` is detached and the live
      node is `widget.View.vue.$el`. Later product updates (e.g. Rebuy re-fetching once cookie
      consent arrives) only match through the live node.
    */
    ownsWidget(widget) {
      return [widget?.element, widget?.View?.vue?.$el].some((node) => node && this.contains(node));
    }

    onProductsChange(event) {
      const {widget, products} = event.detail || {};
      if (!this.ownsWidget(widget)) return;

      this.render(widget, products);
    }

    renderFromHydratedWidget() {
      const widget = window.Rebuy?.widgets?.find((item) => this.ownsWidget(item));
      const products = widget?.data?.products;

      if (products?.length) this.render(widget, products);
    }

    async render(widget, products) {
      const recommended = (products || [])
        .filter((product) => product.handle && product.handle !== this.excludeHandle)
        .slice(0, this.limit);

      if (!recommended.length) return;

      this.widget = widget;
      this.products = new Map(recommended.map((product) => [product.handle, product]));

      this.controller?.abort();
      this.controller = new AbortController();

      let cards = [];

      try {
        cards = await Promise.all(recommended.map((product, index) => this.fetchCard(product.handle, index)));
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
        return;
      }

      const html = cards.filter(Boolean).join('');
      if (!html) return;

      this.grid.innerHTML = html;
      this.wireCards();
      this.content.classList.remove('hidden');

      this.slider?.dispatchEvent(new CustomEvent('theme:grid-slider:init', {bubbles: true}));
      window.SiblingSwatchUpdater?.init(this.grid);
      window.yotpoWidgetsContainer?.initWidgets?.();
    }

    async fetchCard(handle, index) {
      const response = await fetch(`${rootUrl()}products/${handle}?section_id=${CARD_SECTION}`, {
        signal: this.controller.signal,
      });

      if (!response.ok) return '';

      const markup = (await response.text())
        .replaceAll('||itemAnimationDelay||', index * 100)
        .replaceAll('||itemAnimationAnchor||', this.grid.id ? `#${this.grid.id}` : '');

      const content = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-api-content]');
      const card = content?.querySelector('[data-grid-item]');
      if (!card) return '';

      // Which Rebuy recommendation this slot belongs to. It survives sibling swaps (see
      // product-card-sibling-swatch.js), so a swapped card still reports to the right product.
      card.dataset.rebuyHandle = handle;

      return content.innerHTML;
    }

    /*
      The Rebuy product a card should report as.

      Rebuy attributes by handle: learnMore() records the clicked handle and the PDP matches its
      own handle against that record. So after a sibling swap the product has to carry the
      sibling's handle, otherwise the sale on the sibling's PDP is never attributed. The rest of
      the object stays the recommended product, which is what the click event is logged against.
    */
    productForCard(card) {
      const product = this.products.get(card.dataset.rebuyHandle);
      if (!product) return null;

      const handle = handleFromUrl(card.dataset.url) || product.handle;
      return handle === product.handle ? product : {...product, handle};
    }

    canTrack() {
      const {widget} = this;
      const supported = typeof widget?.learnMore === 'function' && typeof widget?.learnMoreURL === 'function';

      if (widget && !supported && !this.warned) {
        this.warned = true;
        console.warn('[rebuy-recommendations] Rebuy widget has no learnMore/learnMoreURL - clicks are not being attributed.');
      }

      return supported;
    }

    wireCards() {
      if (!this.canTrack()) return;

      this.grid.querySelectorAll('[data-grid-item][data-rebuy-handle]').forEach((card) => {
        const product = this.productForCard(card);
        if (!product) return;

        const url = this.widget.learnMoreURL(product);
        card.querySelectorAll('a[data-product-link]').forEach((link) => link.setAttribute('href', url));
      });
    }

    onClick(event) {
      const link = event.target.closest('a[data-product-link]');
      const card = link?.closest('[data-grid-item][data-rebuy-handle]');
      if (!card || !this.grid.contains(card)) return;

      // A modified click opens a new tab from the href, which already carries Rebuy's discovery
      // params - so the sale is still attributed there, only the click event itself is skipped.
      const isModified = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (event.defaultPrevented || isModified) return;

      const product = this.productForCard(card);
      if (!product || !this.canTrack() || !this.widget.hasLearnMore?.()) return;

      // learnMore() tracks and then navigates on its own after a short delay, which is what
      // gives its tracking request time to leave. If it throws, fall back to the plain link.
      event.preventDefault();

      try {
        this.widget.learnMore(product, event);
      } catch (error) {
        console.error(error);
        window.location.href = link.href;
      }
    }
  }

  if (!customElements.get('rebuy-recommendations')) {
    customElements.define('rebuy-recommendations', RebuyRecommendations);

    // Theme editor: a re-rendered section brings a fresh widget node Rebuy has never seen.
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.querySelector('[data-rebuy-id]')) window.Rebuy?.init?.();
    });
  }
})();
